import {
  Activity,
  AppointmentType,
  Campaign,
  Contact,
  ContactBalanceTransaction,
  Conversation,
  Coupon,
  CouponV2,
  Discount,
  DiscountV2,
  EventType,
  Feedback,
  Invite,
  Member,
  Membership,
  MembershipPackage,
  Message,
  Note,
  Order,
  OrderV2,
  Program,
  ProgramAttendee,
  ProgramAttendeeMergeResult,
  ProgramEvent,
  QuickFilter,
  Refund,
  RefundPolicy,
  RefundV2,
  Registration,
  RegistrationOption,
  SessionCredit,
  SessionCreditsUpdatedBalance,
  SessionPackage,
  TaxRate,
  TaxRateV2,
} from '@citruscamps/citrus-client'

export type ActivityEvent =
  | ProgramAttendeeActivity
  | ProgramAttendeeMergeResultActivity
  | CampaignActivity
  | ConversationActivity
  | MessageActivity
  | CouponActivity
  | DiscountActivity
  | ContactActivity
  | ContactAccountBalanceActivity
  | FeebackActivity
  | InviteActivity
  | MemberActivity
  | ProgramActivity
  | OrderActivity
  | RefundActivity
  | ProgramEventActivity
  | RegistrationOptionActivity
  | AppointmentTypeActivity
  | RegistrationActivity
  | TaxRateActivity
  | MembershipActivity
  | SessionCreditsUpdatedBalanceActivity
  | SessionCreditActivity
  | MembershipPackageActivity
  | SessionPackageActivity
  | NoteActivity
  | RefundPolicyActivity
  | QuickFilterActivity

export interface ProgramAttendeeActivity extends Activity {
  type:
    | typeof EventType.program_attendee_created
    | typeof EventType.program_attendee_updated
    | typeof EventType.program_attendee_bulk_created
    | typeof EventType.program_attendee_viewed_health_card
    | typeof EventType.program_attendee_disconnected
  data: ProgramAttendee
}

export interface ProgramAttendeeMergeResultActivity extends Activity {
  type: typeof EventType.program_attendee_merged
  data: ProgramAttendeeMergeResult
}

export interface CampaignActivity extends Activity {
  type:
    | typeof EventType.campaign_created
    | typeof EventType.campaign_updated
    | typeof EventType.campaign_deleted
  data: Campaign
}

export interface ConversationActivity extends Activity {
  type:
    | typeof EventType.conversation_updated
    | typeof EventType.conversation_mark_open
    | typeof EventType.conversation_bulk_mark_open
    | typeof EventType.conversation_bulk_updated
    | typeof EventType.conversation_mark_closed
    | typeof EventType.conversation_bulk_mark_closed
    | typeof EventType.conversation_archive
    | typeof EventType.conversation_bulk_archive
  data: Conversation
}

export interface MessageActivity extends Activity {
  type:
    | typeof EventType.message_created
    | typeof EventType.message_sent
    | typeof EventType.message_bulk_created
  data: Message
}

export interface CouponActivity extends Activity {
  type:
    | typeof EventType.coupon_created
    | typeof EventType.coupon_updated
    | typeof EventType.coupon_deleted
  data: Coupon | CouponV2
}

export interface DiscountActivity extends Activity {
  type:
    | typeof EventType.discount_created
    | typeof EventType.discount_updated
    | typeof EventType.discount_deleted
  data: Discount | DiscountV2
}

export interface ContactActivity extends Activity {
  type:
    | typeof EventType.contact_created
    | typeof EventType.contact_updated
    | typeof EventType.contact_subscribed
    | typeof EventType.contact_unsubscribed
    | typeof EventType.contact_bulk_created
    | typeof EventType.contact_deleted
  data: Contact
}

export interface ContactAccountBalanceActivity extends Activity {
  type:
    | typeof EventType.contact_account_balance_created
    | typeof EventType.contact_account_balance_updated
  data: ContactBalanceTransaction
}

export interface FeebackActivity extends Activity {
  type:
    | typeof EventType.feedback_bulk_created
    | typeof EventType.feedback_updated
    | typeof EventType.feedback_created
  data: Feedback
}

export interface InviteActivity extends Activity {
  type:
    | typeof EventType.invite_created
    | typeof EventType.invite_accepted
    | typeof EventType.invite_deleted
    | typeof EventType.invite_bulk_created
  data: Invite
}

export interface MemberActivity extends Activity {
  type:
    | typeof EventType.member_created
    | typeof EventType.member_updated
    | typeof EventType.member_deleted
  data: Member
}

export interface ProgramActivity extends Activity {
  type: typeof EventType.program_created | typeof EventType.program_updated
  data: Program
}

export interface OrderActivity extends Activity {
  type:
    | typeof EventType.order_created
    | typeof EventType.order_updated
    | typeof EventType.order_processed
    | typeof EventType.order_refunded
    | typeof EventType.order_bulk_created
    | typeof EventType.order_bulk_updated
    | typeof EventType.order_bulk_processed
    | typeof EventType.order_deleted
    | typeof EventType.order_dispute_funds_reinstated
    | typeof EventType.order_dispute_funds_withdrawn
    | typeof EventType.order_dispute_created
    | typeof EventType.order_dispute_updated
    | typeof EventType.order_dispute_closed
  data: Order | OrderV2
}

export interface RefundActivity extends Activity {
  type: typeof EventType.refund_created | typeof EventType.refund_updated
  data: Refund | RefundV2
}

export interface ProgramEventActivity extends Activity {
  type:
    | typeof EventType.program_event_bulk_published
    | typeof EventType.program_event_bulk_updated
    | typeof EventType.program_event_bulk_cancelled
    | typeof EventType.program_event_bulk_created
    | typeof EventType.program_event_updated
    | typeof EventType.program_event_cancelled
    | typeof EventType.program_event_created
    | typeof EventType.program_event_published
    | typeof EventType.program_event_deleted
    | typeof EventType.program_event_bulk_deleted
  data: ProgramEvent
}

export interface RegistrationOptionActivity extends Activity {
  type:
    | typeof EventType.registration_option_created
    | typeof EventType.registration_option_updated
    | typeof EventType.registration_option_deleted
  data: RegistrationOption
}

export interface AppointmentTypeActivity extends Activity {
  type:
    | typeof EventType.appointment_type_created
    | typeof EventType.appointment_type_updated
    | typeof EventType.appointment_type_published
    | typeof EventType.appointment_type_deleted
  data: AppointmentType
}

export interface RegistrationActivity extends Activity {
  type:
    | typeof EventType.appointment_created
    | typeof EventType.appointment_updated
    | typeof EventType.registration_created
    | typeof EventType.registration_bulk_created
    | typeof EventType.registration_updated
    | typeof EventType.registration_deleted
  data: Registration
}

export interface TaxRateActivity extends Activity {
  type:
    | typeof EventType.tax_rates_created
    | typeof EventType.tax_rates_updated
    | typeof EventType.tax_rates_deleted
  data: TaxRate | TaxRateV2
}

export interface MembershipActivity extends Activity {
  type:
    | typeof EventType.membership_created
    | typeof EventType.membership_updated
    | typeof EventType.membership_canceled
    | typeof EventType.membership_bulk_updated
    | typeof EventType.membership_bulk_canceled
  data: Membership
}

export interface SessionCreditsUpdatedBalanceActivity extends Activity {
  type: typeof EventType.session_credit_created
  data: SessionCreditsUpdatedBalance
}

export interface SessionCreditActivity extends Activity {
  type:
    | typeof EventType.session_credit_updated
    | typeof EventType.session_credit_bulk_updated
    | typeof EventType.session_credit_deleted
  data: SessionCredit
}

export interface MembershipPackageActivity extends Activity {
  type:
    | typeof EventType.membership_package_created
    | typeof EventType.membership_package_updated
    | typeof EventType.membership_package_discontinued
    | typeof EventType.membership_package_deleted
  data: MembershipPackage
}

export interface SessionPackageActivity extends Activity {
  type:
    | typeof EventType.session_package_created
    | typeof EventType.session_package_updated
    | typeof EventType.session_package_discontinued
    | typeof EventType.session_package_deleted
  data: SessionPackage
}

export interface NoteActivity extends Activity {
  type: typeof EventType.note_created
  data: Note
}

export interface RefundPolicyActivity extends Activity {
  type: typeof EventType.refund_policy_updated
  data: RefundPolicy
}

export interface QuickFilterActivity extends Activity {
  type:
    | typeof EventType.quick_filter_created
    | typeof EventType.quick_filter_updated
    | typeof EventType.quick_filter_deleted
  data: QuickFilter
}

export const isSupportedActivity = (activity: Activity): activity is ActivityEvent =>
  new Array<EventType>(
    EventType.program_attendee_created,
    EventType.program_attendee_updated,
    EventType.program_attendee_bulk_created,
    EventType.program_attendee_viewed_health_card,
    EventType.program_attendee_disconnected,
    EventType.program_attendee_merged,
    EventType.campaign_created,
    EventType.campaign_updated,
    EventType.campaign_deleted,
    EventType.conversation_updated,
    EventType.conversation_mark_open,
    EventType.conversation_bulk_mark_open,
    EventType.conversation_bulk_updated,
    EventType.conversation_mark_closed,
    EventType.conversation_bulk_mark_closed,
    EventType.conversation_archive,
    EventType.conversation_bulk_archive,
    EventType.message_created,
    EventType.message_sent,
    EventType.message_bulk_created,
    EventType.coupon_created,
    EventType.coupon_updated,
    EventType.coupon_deleted,
    EventType.discount_created,
    EventType.discount_updated,
    EventType.discount_deleted,
    EventType.contact_created,
    EventType.contact_updated,
    EventType.contact_subscribed,
    EventType.contact_unsubscribed,
    EventType.contact_bulk_created,
    EventType.contact_deleted,
    EventType.contact_account_balance_created,
    EventType.contact_account_balance_updated,
    EventType.feedback_bulk_created,
    EventType.feedback_updated,
    EventType.feedback_created,
    EventType.invite_created,
    EventType.invite_accepted,
    EventType.invite_deleted,
    EventType.invite_bulk_created,
    EventType.member_created,
    EventType.member_updated,
    EventType.member_deleted,
    EventType.program_created,
    EventType.program_updated,
    EventType.order_created,
    EventType.order_updated,
    EventType.order_processed,
    EventType.order_refunded,
    EventType.order_bulk_created,
    EventType.order_bulk_updated,
    EventType.order_bulk_processed,
    EventType.order_deleted,
    EventType.order_dispute_funds_reinstated,
    EventType.order_dispute_funds_withdrawn,
    EventType.order_dispute_created,
    EventType.order_dispute_updated,
    EventType.order_dispute_closed,
    EventType.refund_created,
    EventType.refund_updated,
    EventType.program_event_bulk_published,
    EventType.program_event_bulk_updated,
    EventType.program_event_bulk_cancelled,
    EventType.program_event_bulk_created,
    EventType.program_event_updated,
    EventType.program_event_cancelled,
    EventType.program_event_created,
    EventType.program_event_published,
    EventType.program_event_deleted,
    EventType.program_event_bulk_deleted,
    EventType.registration_option_created,
    EventType.registration_option_updated,
    EventType.registration_option_deleted,
    EventType.appointment_type_created,
    EventType.appointment_type_updated,
    EventType.appointment_type_published,
    EventType.appointment_type_deleted,
    EventType.appointment_created,
    EventType.appointment_updated,
    EventType.registration_created,
    EventType.registration_bulk_created,
    EventType.registration_updated,
    EventType.registration_deleted,
    EventType.tax_rates_created,
    EventType.tax_rates_updated,
    EventType.tax_rates_deleted,
    EventType.membership_created,
    EventType.membership_updated,
    EventType.membership_canceled,
    EventType.membership_bulk_updated,
    EventType.membership_bulk_canceled,
    EventType.session_credit_created,
    EventType.session_credit_updated,
    EventType.session_credit_bulk_updated,
    EventType.session_credit_deleted,
    EventType.membership_package_created,
    EventType.membership_package_updated,
    EventType.membership_package_discontinued,
    EventType.membership_package_deleted,
    EventType.session_package_created,
    EventType.session_package_updated,
    EventType.session_package_discontinued,
    EventType.session_package_deleted,
    EventType.note_created,
    EventType.refund_policy_updated,
    EventType.quick_filter_created,
    EventType.quick_filter_updated,
    EventType.quick_filter_deleted,
  ).includes(activity.type)
